import type { ReactNode, RefObject } from "react";

import { Box, Flex } from "@radix-ui/themes";
import ReactGridLayout, { type Layout } from "react-grid-layout";

import "react-grid-layout/css/styles.css";

import { cn } from "~/v2/utils/cn";
import { WIDGET_DRAG_HANDLE_SELECTOR } from "~/v3-poc/components/Widget/dragHandle";
import {
  ORDER_TICKET_DRAWER_SLOT_HEIGHT,
  ORDER_TICKET_DRAWER_SLOT_TOP,
  ORDER_TICKET_DRAWER_SLOT_Z_INDEX,
} from "../orderTicketDrawer/OrderTicketDrawer.types";
import {
  WorkspaceTitleLeadingPortal,
  WorkspaceToolbarPortal,
} from "../shared/workspaceToolbarSlot";
import type { AppliedReactGridLayoutProps } from "./settings/reactGridLayoutSettings.adapter";
import "./reactGridLayoutTab.css";

export interface ReactGridLayoutTabPresentationProps {
  width: number;
  mounted: boolean;
  containerRef: RefObject<HTMLDivElement | null>;
  layout: Layout;
  onLayoutChange: (layout: Layout) => void;
  /** Renders the workspace-connected widget for the given id. */
  renderWidget: (id: string) => ReactNode;
  appliedSettings: AppliedReactGridLayoutProps;
  /** Connector-built toolbar, rendered into the workspace header slot. */
  toolbar: ReactNode;
  drawerReservedWidth?: string;
  drawerTransition?: string;
  /**
   * Receives the drawer slot element. A callback ref (rather than a ref
   * object) lets the connector hold the element in state, so the drawer's
   * portal container is never a stale `null` at open time.
   */
  drawerSlotRef?: (element: HTMLDivElement | null) => void;
  drawer?: ReactNode;
  /** Workspace group selector, rendered beside the workspace name. */
  workspaceGroupSelect?: ReactNode;
}

/**
 * Toolbar popover → viewport shell with a right-side drawer slot.
 *
 * The Radix `<Flex>` provides the column/gap layout. The engine region is a
 * two-column grid: the RGL canvas on the left and the sticky order-ticket
 * drawer slot on the right. The inner canvas uses `min-w-0` so measured width
 * can shrink when the drawer reserves space in push mode.
 *
 * The drawer slot is sticky and `align-self: start` at the shared slot
 * offset/height/stacking level (see `ORDER_TICKET_DRAWER_SLOT_*`), so the
 * pushed drawer pins below the fixed header and scrolls in its own area.
 *
 * The sole viewport height (`h-dvh`) is owned by the Theme root; the flex
 * chain from the page down carries the space here. `overflow-x-auto` on the
 * canvas handles a desktop grid wider than the frame.
 */
export const ReactGridLayoutTabPresentation = ({
  width,
  mounted,
  containerRef,
  layout,
  onLayoutChange,
  renderWidget,
  appliedSettings,
  toolbar,
  drawerReservedWidth = "0px",
  drawerTransition = "none",
  drawerSlotRef,
  drawer,
  workspaceGroupSelect,
}: ReactGridLayoutTabPresentationProps) => {
  // Gap only while the drawer column has width — a permanent column-gap would
  // leave empty space on the right when the reserved track is `0px`.
  const drawerOpen = drawerReservedWidth !== "0px";

  return (
    <Flex direction="column" gap="3" flexGrow="1" className="min-h-0">
      {workspaceGroupSelect ? (
        <WorkspaceTitleLeadingPortal>
          {workspaceGroupSelect}
        </WorkspaceTitleLeadingPortal>
      ) : null}

      <WorkspaceToolbarPortal>{toolbar}</WorkspaceToolbarPortal>

      <Box
        data-testid="rgl-engine-region"
        // Static layout via Tailwind; only the reserved-track width is runtime
        // dynamic (`0px` ↔ `clamp(…)`), so that stays on `style`.
        className={cn(
          "grid min-h-0 min-w-0 grow",
          // Match the tab shell's `gap="3"` while the drawer column has width.
          drawerOpen && "gap-x-3",
          drawerTransition === "none"
            ? "transition-none"
            : "transition-[grid-template-columns] duration-200 ease-in-out",
        )}
        style={{
          gridTemplateColumns: `minmax(0, 1fr) ${drawerReservedWidth}`,
        }}
      >
        <Flex
          data-workspace-viewport="rgl"
          className="min-h-0 min-w-0 overflow-x-auto rounded-[var(--radius-2)] border border-[color:var(--gray-a5)] bg-[var(--color-panel-solid)]"
          flexGrow="1"
          direction="column"
        >
          <Box
            ref={containerRef as RefObject<HTMLDivElement>}
            // rem-based pb follows Radix's typographic scale so a user zoom or
            // a --font-size-* override resizes the drop-zone runway
            // proportionally rather than staying pinned to raw pixels.
            // `min-w-0` lets the measured container shrink with the drawer push.
            className="workspace-rgl min-w-0 shrink-0 grow pb-[10rem]"
            p="2"
          >
            {mounted && (
              <ReactGridLayout
                className="layout"
                width={width}
                layout={layout}
                gridConfig={appliedSettings.gridConfig}
                dragConfig={{
                  ...appliedSettings.dragConfig,
                  enabled: true,
                  handle: WIDGET_DRAG_HANDLE_SELECTOR,
                }}
                resizeConfig={{
                  ...appliedSettings.resizeConfig,
                  enabled: true,
                }}
                dropConfig={appliedSettings.dropConfig}
                positionStrategy={appliedSettings.positionStrategy}
                compactor={appliedSettings.compactor}
                constraints={appliedSettings.constraints}
                autoSize={appliedSettings.autoSize}
                onLayoutChange={onLayoutChange}
              >
                {layout.map((item) => (
                  <div key={item.i}>{renderWidget(item.i)}</div>
                ))}
              </ReactGridLayout>
            )}
          </Box>
        </Flex>
        <Box
          ref={drawerSlotRef}
          data-testid="rgl-drawer-slot"
          style={{
            position: "sticky",
            top: ORDER_TICKET_DRAWER_SLOT_TOP,
            alignSelf: "start",
            height: ORDER_TICKET_DRAWER_SLOT_HEIGHT,
            zIndex: ORDER_TICKET_DRAWER_SLOT_Z_INDEX,
          }}
        >
          {drawer}
        </Box>
      </Box>
    </Flex>
  );
};
